import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useLanguage } from '@/contexts/LanguageContext';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

interface CookiePreferencesProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (preferences: CookiePreferences) => void;
}

const CookiePreferences = ({ open, onOpenChange, onSave }: CookiePreferencesProps) => {
  const { t } = useLanguage();
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const stored = localStorage.getItem('cookiePreferences');
    if (stored) {
      try {
        setPreferences(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse cookie preferences');
      }
    }
  }, [open]);

  const handleSave = () => {
    onSave(preferences);
    onOpenChange(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    onSave(allAccepted);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] bg-card">
        <DialogHeader>
          <DialogTitle className="text-xl">{t('cookiePreferences.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('cookiePreferences.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Necessary Cookies */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="space-y-1 flex-1">
                <Label htmlFor="necessary" className="text-base font-semibold">
                  {t('cookiePreferences.necessary.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookiePreferences.necessary.description')}
                </p>
              </div>
              <Switch
                id="necessary"
                checked={preferences.necessary}
                disabled
                className="ml-4"
              />
            </div>
          </div>

          {/* Analytics Cookies */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="space-y-1 flex-1">
                <Label htmlFor="analytics" className="text-base font-semibold">
                  {t('cookiePreferences.analytics.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookiePreferences.analytics.description')}
                </p>
              </div>
              <Switch
                id="analytics"
                checked={preferences.analytics}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, analytics: checked })
                }
                className="ml-4"
              />
            </div>
          </div>

          {/* Marketing Cookies */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="space-y-1 flex-1">
                <Label htmlFor="marketing" className="text-base font-semibold">
                  {t('cookiePreferences.marketing.title')}
                </Label>
                <p className="text-sm text-muted-foreground">
                  {t('cookiePreferences.marketing.description')}
                </p>
              </div>
              <Switch
                id="marketing"
                checked={preferences.marketing}
                onCheckedChange={(checked) =>
                  setPreferences({ ...preferences, marketing: checked })
                }
                className="ml-4"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleSave}
            className="w-full sm:w-auto"
          >
            {t('cookiePreferences.savePreferences')}
          </Button>
          <Button
            onClick={handleAcceptAll}
            className="w-full sm:w-auto"
          >
            {t('cookiePreferences.acceptAll')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CookiePreferences;
